import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';

// Twitch brand color
const TWITCH_PURPLE = '#9146FF';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithTwitch, user, isLoading } = useAuth();

  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Handle OAuth errors from callback
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
      // Clean up URL
      window.history.replaceState({}, '', '/admin/login');
    }
  }, [searchParams]);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !isLoading) {
      const redirect = searchParams.get('redirect') || '/admin/dashboard';
      navigate(redirect);
    }
  }, [user, isLoading, navigate, searchParams]);

  const handleTwitchLogin = () => {
    setError('');
    setIsRedirecting(true);
    loginWithTwitch();
  };

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-gradient-bg">
        <div className="text-white font-roboto">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center admin-gradient-bg">
      <motion.div
        className="bg-black/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-neon-gold/50 w-full max-w-md shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-press-start text-2xl text-neon-gold mb-4">
            WINDROP
          </h1>
          <p className="font-roboto text-base text-white/60">
            Admin Panel
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p className="font-roboto text-red-400 text-center text-sm">{error}</p>
          </motion.div>
        )}

        {/* Twitch Login Button */}
        <button
          onClick={handleTwitchLogin}
          disabled={isRedirecting}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-lg font-roboto font-medium text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: isRedirecting ? `${TWITCH_PURPLE}80` : TWITCH_PURPLE,
          }}
          onMouseEnter={(e) => {
            if (!isRedirecting) {
              e.currentTarget.style.backgroundColor = '#7B2BF5';
            }
          }}
          onMouseLeave={(e) => {
            if (!isRedirecting) {
              e.currentTarget.style.backgroundColor = TWITCH_PURPLE;
            }
          }}
        >
          {/* Twitch Logo SVG */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
          </svg>
          {isRedirecting ? 'Redirecting to Twitch...' : 'Login with Twitch'}
        </button>

        {/* Info Text */}
        <div className="mt-6 text-center">
          <p className="font-roboto text-xs text-white/40">
            Sign in with your Twitch account to access the admin panel.
          </p>
          <p className="font-roboto text-xs text-white/40 mt-2">
            Only authorized users can access this area.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="font-roboto text-xs text-white/30">
            Windrop API v2.0
          </p>
        </div>
      </motion.div>
    </div>
  );
}
